import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { menu, routeForSlug, isPageBuilt, filterMenuForRoles } from '../../config/menuData.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { roleKeysOf } from '../../config/roles.js';
import { useLocale } from '../../context/LocaleContext.jsx';
import { useNavLabel } from './NavMenu.jsx';
import { isAdminNode, partitionNav } from './navModel.js';
import { useFeatures, pruneMenuForFeatures } from '../../services/features.js';

/**
 * The studio's primary navigation, as a vertical rail.
 *
 * It replaces the top navbar's menu because a horizontal bar cannot hold this
 * app's sections: there are ten top-level entries, several with children, and
 * below ~1400px they either wrap onto a second row or collapse behind a
 * hamburger — which is how the nav became unusable at ordinary laptop widths.
 * A rail scales with the number of sections instead of competing with them,
 * and it lets a group's children stay visible rather than living in a popover
 * that closes when the pointer drifts.
 *
 * Everything here is driven by src/config/menuData.js exactly as the top nav
 * was — same labels, icons, grouping, order, slugs and role filtering. Only the
 * presentation changed. Administration is partitioned to the foot of the rail
 * (see navModel.partitionNav) so operator tooling reads as separate from the
 * creative work rather than sitting between two creative sections.
 */

function SidebarIcon({ icon }) {
  if (!icon) return null;
  return <i className={`${icon} app-side__icon`} aria-hidden="true" />;
}

function SidebarLeaf({ item, label, onNavigate, nested = false }) {
  const route = routeForSlug(item.slug);
  const built = isPageBuilt(item.slug);
  // An entry that fronts several routes (Library → Media / Generations /
  // Projects tabs) stays lit on all of them, not just the one it links to.
  const { pathname } = useLocation();
  const alsoActive = (item.alsoActive || []).some((p) => pathname === p || pathname.startsWith(`${p}/`));

  /**
   * A capability we have PROVEN but cannot serve yet.
   *
   * Different from "unbuilt", and worth its own state. Hiding it would
   * understate what the platform can do; linking it would be a promise that
   * breaks on click — the dead-entry problem we removed elsewhere. Showing it
   * greyed with the actual blocker named is the honest third option: the
   * capability is real, the constraint is known, and nobody discovers either by
   * clicking and failing.
   */
  return (
    <li>
      <NavLink
        to={route}
        end
        className={({ isActive }) =>
          [
            'app-side__link',
            nested ? 'app-side__link--nested' : '',
            isActive || alsoActive ? 'active' : '',
            built ? '' : 'app-side__link--unbuilt',
          ]
            .filter(Boolean)
            .join(' ')
        }
        title={built ? undefined : `${label} — page not generated yet`}
        aria-disabled={built ? undefined : 'true'}
        data-slug={item.slug ?? undefined}
        onClick={onNavigate}
      >
        <SidebarIcon icon={item.icon} />
        <span className="app-side__label">{label}</span>
        {/* The page opens normally; the tag flags that one action inside it is
            unavailable, so the constraint is visible before you go looking. */}
        {item.constrained ? (
          <span className="app-side__blocked-tag">Preview</span>
        ) : null}
      </NavLink>
    </li>
  );
}

/**
 * A collapsible section. It opens automatically when it contains the current
 * route, so deep-linking or a page refresh never leaves you looking at a
 * collapsed rail with no idea where you are.
 */
function SidebarGroup({ item, navLabel, onNavigate }) {
  const location = useLocation();
  const holdsActiveRoute = useMemo(
    () => item.children.some((child) => child.slug && location.pathname === routeForSlug(child.slug)),
    [item.children, location.pathname]
  );
  const [open, setOpen] = useState(holdsActiveRoute);

  useEffect(() => {
    if (holdsActiveRoute) setOpen(true);
  }, [holdsActiveRoute]);

  return (
    <li className="app-side__group">
      <button
        type="button"
        className={`app-side__link app-side__toggle${holdsActiveRoute ? ' app-side__toggle--current' : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <SidebarIcon icon={item.icon} />
        <span className="app-side__label">{navLabel(item.label)}</span>
        <i
          className={`fa fa-chevron-${open ? 'down' : 'right'} app-side__chevron`}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <ul className="app-side__sublist">
          {item.children.map((child) => (
            <SidebarLeaf
              key={child.label}
              item={child}
              label={navLabel(child.label)}
              onNavigate={onNavigate}
              nested
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function SidebarNode({ item, navLabel, onNavigate }) {
  return item.children && item.children.length > 0 ? (
    <SidebarGroup item={item} navLabel={navLabel} onNavigate={onNavigate} />
  ) : (
    <SidebarLeaf item={item} label={navLabel(item.label)} onNavigate={onNavigate} />
  );
}

export default function AppSidebar({
  items = menu.children,
  open = false,
  collapsed = false,
  onToggleCollapse,
  onNavigate,
}) {
  const { user } = useAuth();
  const { t } = useLocale();
  const navLabel = useNavLabel(t);
  const roleKeys = useMemo(() => roleKeysOf(user), [user]);

  // Feature-flagged entries (Talking Avatar behind FEATURE_TALKING_AVATAR)
  // are pruned after role filtering; the flag only ever hides, default on.
  const features = useFeatures();
  const visibleItems = useMemo(
    () => pruneMenuForFeatures(filterMenuForRoles(items, roleKeys), features),
    [items, roleKeys, features],
  );
  const { primary, admin } = useMemo(() => partitionNav(visibleItems), [visibleItems]);

  return (
    <nav
      className={`app-side${open ? ' app-side--open' : ''}${collapsed ? ' app-side--collapsed' : ''}`}
      aria-label="Primary"
      id="app-sidebar"
    >
      <ul className="app-side__list">
        {primary.map((item) => (
          <SidebarNode key={item.label} item={item} navLabel={navLabel} onNavigate={onNavigate} />
        ))}
      </ul>

      {admin.length > 0 ? (
        <>
          <div className="app-side__divider" role="separator" />
          <div className="app-side__caption">Administration</div>
          <ul className="app-side__list app-side__list--admin">
            {admin.map((item) => (
              <SidebarNode key={item.label} item={item} navLabel={navLabel} onNavigate={onNavigate} />
            ))}
          </ul>
        </>
      ) : null}

      {/* Collapse to an icon rail. The generator screens (Generation Studio,
          Reel Generator) need the horizontal room for canvases and parameter
          panels, so this is pinned to the bottom where it is always reachable
          rather than scrolling away with the menu. */}
      {onToggleCollapse ? (
        <button
          type="button"
          className="app-side__collapse d-none d-lg-flex"
          onClick={onToggleCollapse}
          aria-expanded={!collapsed}
          aria-controls="app-sidebar"
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          <i
            className={`fa fa-angles-${collapsed ? 'right' : 'left'} app-side__icon`}
            aria-hidden="true"
          />
          <span className="app-side__label">Collapse</span>
        </button>
      ) : null}

      {/* Attribution, pinned to the foot of the rail. Collapsed it becomes the
          mark alone — the same treatment every other rail item gets, so it
          never widens the icon rail or forces a horizontal scroll. */}
      <a
        className="app-side__poweredby"
        href="https://xamun.ai"
        target="_blank"
        rel="noreferrer noopener"
        title="Powered by Xamun"
      >
        <span className="app-side__xamun" aria-hidden="true">X</span>
        <span className="app-side__label app-side__poweredby-text">
          Powered by <strong>Xamun</strong>
        </span>
      </a>
    </nav>
  );
}

export { isAdminNode };
